import { Plus, Info } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/site/PageHeader";
import { CartPanel } from "../components/order/CartPanel";
import { Supplements } from "./PizzasPage";
import { useContent, usePizzas, usePates } from "../lib/content";
import { useCart, useCartTotals } from "../lib/cart";
import { formatPrice, restaurant } from "../data/siteData";
import { scrollToTarget } from "../lib/lenis";

const ProductRow = ({ product, onAdd }) => (
  <li className="flex items-center justify-between gap-4 py-4 border-b border-[#D4A359]/10" data-testid={`order-product-${product.id}`}>
    <div className="min-w-0">
      <p className="font-display text-lg text-[#F7F4EB]">{product.name}</p>
      {product.ingredients && <p className="text-sm text-[#A2B3A7] truncate sm:whitespace-normal">{product.ingredients}</p>}
    </div>
    <div className="flex items-center gap-3 shrink-0">
      <span className="font-semibold text-[#E5B66C]">{formatPrice(product.price)}</span>
      <button type="button" onClick={() => onAdd(product)} className="w-10 h-10 rounded-full bg-[#D4A359] text-[#0B2216] flex items-center justify-center hover:bg-[#E5B66C] transition-colors" aria-label={`Ajouter ${product.name}`} data-testid={`order-add-${product.id}`}>
        <Plus className="w-4 h-4" />
      </button>
    </div>
  </li>
);

const MobileCartBar = () => {
  const { count, total } = useCartTotals();
  if (!count) return null;
  return (
    <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-[#D4A359]/25 bg-[#0B2216]/95 backdrop-blur-xl p-3">
      <button type="button" onClick={() => scrollToTarget(document.getElementById("panier"), -90)} className="btn-gold w-full" data-testid="mobile-cart-bar">
        Voir le panier ({count}) · {formatPrice(total)}
      </button>
    </div>
  );
};

const ProductBlock = ({ title, testId, children }) => (
  <div className="rounded-3xl border border-[#D4A359]/20 bg-[#0E2A1B] p-6 sm:p-8" data-testid={testId}>
    <h2 className="font-display text-3xl">{title}</h2>
    {children}
  </div>
);

export default function OrderPage() {
  const { status } = useContent();
  const pizzas = usePizzas().filter((p) => p.price != null);
  const pates = usePates().filter((p) => p.price != null);
  const { add } = useCart();
  const onAddPizza = (p) => {
    add(p.id);
    toast.success(`${p.name} ajoutée au panier`);
  };
  const onAddPates = (p) => {
    add(p.id);
    toast.success(`Ajouté au panier : ${p.name}`);
  };
  return (
    <>
      <PageHeader testId="order-header" eyebrow="Click & Collect" lines={["Commander", <em key="o" className="text-[#E5B66C]">à emporter</em>]} intro="Pizzas et pâtes à emporter à Morangis : composez votre commande, choisissez le jour et l’heure de retrait, puis envoyez-la au restaurant par WhatsApp. Vous payez sur place, au retrait." />
      <section className="pb-28 sm:pb-32">
        <div className="container-site">
          <p className="mb-6 flex items-start gap-2 rounded-2xl border border-[#D4A359]/40 bg-[#D4A359]/10 p-4 text-sm text-[#F7F4EB]" data-testid="order-payment-info">
            <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#D4A359]" /> Aucun paiement en ligne : votre commande se règle au restaurant, lors du retrait.
          </p>
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-start">
            <div className="lg:col-span-7 space-y-8">
              <ProductBlock title="Les pizzas" testId="order-pizzas-block">
                <div className="mt-4"><Supplements /></div>
                <p className="mt-3 text-xs text-[#A2B3A7]">Ajoutez les suppléments dans le panier, puis précisez-les dans les remarques.</p>
                {status === "error" && <p className="mt-6 text-[#F0A385]">Commande en ligne momentanément indisponible. Appelez-nous au {restaurant.phone}.</p>}
                <ul className="mt-4">{pizzas.map((p) => <ProductRow key={p.id} product={p} onAdd={onAddPizza} />)}</ul>
              </ProductBlock>
              {pates.length > 0 && (
                <ProductBlock title="Les pâtes" testId="order-pates-block">
                  <p className="mt-3 text-xs text-[#A2B3A7]">Au choix : spaghetti, tagliatelle ou penne. Précisez-le dans les remarques.</p>
                  <ul className="mt-4">{pates.map((p) => <ProductRow key={p.id} product={p} onAdd={onAddPates} />)}</ul>
                </ProductBlock>
              )}
            </div>
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <CartPanel />
            </div>
          </div>
        </div>
      </section>
      <MobileCartBar />
    </>
  );
}
